import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Car, Pencil, X, Plus, Phone, Siren, Mail } from "lucide-react";
import { Btn, inp, inpSm, Field, fmtPhone, Empty } from "./ui";

/* Account standing (delinquency) — per-property flag the concierge honours for amenities / RSVPs. */
export function StandingBlock({ tenantId, household, readOnly, compact = false }) {
  const [st, setSt] = useState(household?.standing === "restricted" ? "restricted" : "good");
  const [meta, setMeta] = useState({ reason: household?.standing_reason || "", at: household?.standing_at || "", by: household?.standing_by || "" });
  const [restricting, setRestricting] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSt(household?.standing === "restricted" ? "restricted" : "good");
    setMeta({ reason: household?.standing_reason || "", at: household?.standing_at || "", by: household?.standing_by || "" });
    setRestricting(false); setReason("");
  }, [household]);
  if (!household?.id) return null;
  const save = async (standing) => {
    if (standing === "restricted" && !reason.trim()) { toast.error("A written reason is required (due process)"); return; }
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/households/${household.id}/standing`, { standing, reason: reason.trim() });
      setSt(standing); setMeta({ reason: standing === "restricted" ? reason.trim() : "", at: new Date().toISOString(), by: "you" });
      setRestricting(false); setReason("");
      toast.success(standing === "restricted" ? "Account restricted" : "Good standing restored", { description: `The ${V.lc("household")} was sent a neutral text notice.` });
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
    finally { setBusy(false); }
  };
  const restricted = st === "restricted";
  return (
    <div data-testid="pf-standing-section">
      <div className={`rounded-xl border px-3.5 py-3 text-[13px] ${restricted ? "bg-red-50 border-red-200" : "bg-emerald-50 border-emerald-200"}`}>
        <div className="flex items-center justify-between gap-2">
          <span className={`inline-flex items-center gap-2 font-bold ${restricted ? "text-red-800" : "text-emerald-800"}`} data-testid="pf-standing-badge">{restricted ? <ShieldAlert className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}{restricted ? "Restricted" : "In good standing"}</span>
          {!readOnly && !restricting && (restricted
            ? <Btn size="sm" variant="dark" disabled={busy} onClick={() => save("good")} data-testid="pf-standing-restore">Restore good standing</Btn>
            : <Btn size="sm" variant="outline" onClick={() => setRestricting(true)} data-testid="pf-standing-restrict">Mark as restricted…</Btn>)}
        </div>
        {restricted && meta.reason && <p className="mt-1.5 text-zinc-700">{meta.reason}</p>}
        {restricted && meta.at && <p className="mt-1 text-[12px] text-zinc-500">Set by {meta.by || "staff"} · {new Date(meta.at).toLocaleDateString()}</p>}
        {restricting && (
          <div className="mt-3 space-y-2">
            <input data-testid="pf-standing-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`Written reason (required — e.g. ${V.lcs("dues")} 90+ days past due)`} className={inp} />
            <div className="flex gap-2">
              <Btn size="sm" variant="primary" disabled={busy} onClick={() => save("restricted")} data-testid="pf-standing-confirm">Restrict account</Btn>
              <Btn size="sm" variant="ghost" onClick={() => { setRestricting(false); setReason(""); }}>Cancel</Btn>
            </div>
            <p className="text-[12px] text-zinc-500">The {V.lc("household")} gets a neutral text notice (no amounts mentioned). Make sure your governing documents authorize this first.</p>
          </div>
        )}
      </div>
      {!compact && <p className="mt-3 text-[13px] text-zinc-500">{restricted ? "The concierge holds amenity bookings for this property and routes requests to you first. Residents are never told why." : "Restricting a property tells the concierge to hold amenity bookings and route requests to you first. A written reason is required."} Maintenance requests, safety alerts and portal access are never blocked.</p>}
    </div>
  );
}

const VEHICLE_TYPE_OPTS = ["car", "truck", "suv", "van", "motorcycle", "vendor", "other"];
export function VehiclesBlock({ tenantId, household, readOnly }) {
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const hid = household?.id;
  const emptyForm = { make: "", model: "", color: "", year: "", plate: "", plate_state: "", vehicle_type: "" };
  const load = useCallback(() => { if (!hid) return; api.get(`/tenants/${tenantId}/households/${hid}/vehicles`).then((r) => setVehicles(r.data.vehicles || [])).catch(() => setVehicles([])); }, [tenantId, hid]);
  useEffect(() => { setForm(null); load(); }, [load]);
  if (!hid) return <Empty>No property on file yet — vehicles live on the property.</Empty>;
  const save = async () => {
    const f = form;
    if (!(f.plate || "").trim() && !(f.make || "").trim() && !(f.model || "").trim()) { toast.error("Add at least a plate or a make/model"); return; }
    setBusy(true);
    try {
      if (f.id) await api.put(`/tenants/${tenantId}/vehicles/${f.id}`, f); else await api.post(`/tenants/${tenantId}/households/${hid}/vehicles`, f);
      toast.success(f.id ? "Vehicle updated" : "Vehicle added"); setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const remove = async (v) => {
    setBusy(true);
    try { await api.delete(`/tenants/${tenantId}/vehicles/${v.id}`); toast.success("Vehicle removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    finally { setBusy(false); }
  };
  const vdesc = (v) => [v.color, v.year, v.make, v.model].filter(Boolean).join(" ") || (v.vehicle_type || "Vehicle");
  return (
    <div data-testid="pf-vehicles-section">
      {!readOnly && !form && <div className="flex justify-end mb-3"><Btn size="sm" variant="dark" onClick={() => setForm({ ...emptyForm })} data-testid="pf-add-vehicle-btn"><Plus /> Add vehicle</Btn></div>}
      {vehicles.length === 0 && !form && <Empty>No vehicles on file for this property.</Empty>}
      {vehicles.length > 0 && (
        <table className="w-full text-left text-[14px]">
          <thead className="text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-400"><tr className="h-9"><th>Vehicle</th><th>Plate</th><th className="hidden sm:table-cell">Type</th><th /></tr></thead>
          <tbody>
            {vehicles.map((v) => (
              <tr key={v.id} data-testid={`pf-vehicle-${v.id}`} className="h-12 border-t border-zinc-100">
                <td className="font-bold">{vdesc(v)}</td>
                <td>{v.plate ? <span className="font-mono text-zinc-700">{v.plate}{v.plate_state ? ` · ${v.plate_state}` : ""}</span> : <span className="text-zinc-300">No plate</span>}</td>
                <td className="hidden sm:table-cell text-zinc-600 capitalize">{v.vehicle_type || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  {!readOnly && <span className="inline-flex gap-1">
                    <button type="button" data-testid={`pf-edit-vehicle-${v.id}`} title="Edit vehicle" onClick={() => setForm({ ...v })} className="grid h-8 w-8 place-items-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900"><Pencil className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`pf-remove-vehicle-${v.id}`} title="Remove vehicle" disabled={busy} onClick={() => remove(v)} className="grid h-8 w-8 place-items-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>
                  </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {form && (
        <div data-testid="pf-vehicle-form" className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3.5 space-y-2.5">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            <input data-testid="pf-vehicle-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="Color" className={inpSm} />
            <input data-testid="pf-vehicle-year" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" className={inpSm} />
            <input data-testid="pf-vehicle-make" value={form.make} onChange={(e) => setForm({ ...form, make: e.target.value })} placeholder="Make (Toyota)" className={inpSm} />
            <input data-testid="pf-vehicle-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} placeholder="Model (Camry)" className={inpSm} />
            <input data-testid="pf-vehicle-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} placeholder="License plate" className={`${inpSm} font-mono uppercase placeholder:normal-case placeholder:font-sans`} />
            <input data-testid="pf-vehicle-state" value={form.plate_state} onChange={(e) => setForm({ ...form, plate_state: e.target.value })} placeholder="State (TX)" maxLength={4} className={`${inpSm} uppercase placeholder:normal-case`} />
          </div>
          <select data-testid="pf-vehicle-type" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })} className={`${inpSm} w-full capitalize`}>
            <option value="">Type (optional)</option>
            {VEHICLE_TYPE_OPTS.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
          </select>
          <div className="flex gap-2"><Btn size="sm" variant="dark" disabled={busy} onClick={save} data-testid="pf-vehicle-save">{form.id ? "Save changes" : "Add vehicle"}</Btn><Btn size="sm" variant="ghost" onClick={() => setForm(null)}>Cancel</Btn></div>
        </div>
      )}
    </div>
  );
}

const PHONE_LABEL_OPTS = ["mobile", "home", "work", "other"];
/* Extra labeled numbers beyond the primary mobile the concierge texts. */
export function PhonesBlock({ tenantId, person, readOnly }) {
  const [phones, setPhones] = useState(person?.phones || []);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ label: "home", number: "" });
  const [busy, setBusy] = useState(false);
  const pid = person?.id;
  const digits = (s) => (s || "").replace(/\D/g, "");
  useEffect(() => { setPhones(person?.phones || []); setAdding(false); setForm({ label: "home", number: "" }); }, [person?.id]); // eslint-disable-line
  if (!pid) return null;
  const add = async () => {
    if (digits(form.number).length < 10) { toast.error("Enter a valid phone number"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/people/${pid}/phones`, { label: form.label, number: form.number.trim() });
      if (r.data.already_primary) toast.info("That's already the primary number");
      else if (r.data.added) { setPhones([...phones.filter((p) => digits(p.number) !== digits(r.data.added.number)), r.data.added]); toast.success("Number added"); }
      else if (r.data.updated) { setPhones(phones.map((p) => digits(p.number) === digits(r.data.phone.number) ? { ...p, label: r.data.phone.label } : p)); toast.success("Number updated"); }
      setForm({ label: "home", number: "" }); setAdding(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const remove = async (ph) => {
    setBusy(true);
    try { await api.post(`/tenants/${tenantId}/people/${pid}/phones`, { number: ph.number, remove: true }); setPhones(phones.filter((x) => x !== ph)); toast.success("Number removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="pf-phones-section">
      <ul className="space-y-2">
        <li className="flex items-center gap-3 rounded-xl border border-zinc-200 px-3.5 py-3">
          <Phone className="h-4 w-4 text-zinc-400 shrink-0" />
          <span className={`font-mono text-[14px] ${person.phone ? "" : "text-zinc-300"}`}>{person.phone ? fmtPhone(person.phone) : "No mobile on file"}</span>
          <span className="text-[12px] text-zinc-500">Mobile · primary</span>
          {person.phone && <span className="ml-auto text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5 whitespace-nowrap">Concierge texts this</span>}
        </li>
        {phones.map((ph, i) => (
          <li key={i} data-testid={`pf-phone-${i}`} className="flex items-center gap-3 rounded-xl border border-zinc-200 px-3.5 py-3 flex-wrap">
            <Phone className="h-4 w-4 text-zinc-400 shrink-0" />
            <span className="font-mono text-[14px]">{fmtPhone(ph.number)}</span>
            <span className="text-[12px] text-zinc-500 capitalize">{ph.label || "other"}</span>
            {ph.verified
              ? <span data-testid={`pf-phone-${i}-verified`} title={`Verified${ph.verified_via ? ` · ${String(ph.verified_via).replace(/_/g, " ")}` : ""}`} className="rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5">Verified</span>
              : <span data-testid={`pf-phone-${i}-unverified`} title="Recognised, but they haven't proven they hold this number yet" className="rounded-full bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5">Unverified</span>}
            {ph.opted_out && <span data-testid={`pf-phone-${i}-stop`} className="rounded-full bg-red-50 text-red-700 text-[10px] font-bold px-2 py-0.5">STOP</span>}
            {!readOnly && <button type="button" onClick={() => remove(ph)} disabled={busy} title="Remove number" className="ml-auto grid h-8 w-8 place-items-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
      {!readOnly && !adding && <Btn size="sm" variant="ghost" className="mt-2 -ml-3" onClick={() => setAdding(true)} data-testid="pf-add-phone-btn"><Plus /> Add another number</Btn>}
      {adding && (
        <div data-testid="pf-phone-form" className="mt-2.5 flex items-center gap-2 flex-wrap">
          <select data-testid="pf-phone-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className={`${inpSm} capitalize`}>
            {PHONE_LABEL_OPTS.map((l) => <option key={l} value={l} className="capitalize">{l}</option>)}
          </select>
          <input data-testid="pf-phone-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} placeholder="(512) 555-0123" className={`${inpSm} flex-1 min-w-[160px] font-mono`} />
          <Btn size="sm" variant="dark" disabled={busy} onClick={add} data-testid="pf-phone-save">Add</Btn>
          <Btn size="sm" variant="ghost" onClick={() => { setAdding(false); setForm({ label: "home", number: "" }); }}>Cancel</Btn>
        </div>
      )}
      <p className="mt-3 text-[12px] text-zinc-400">The concierge recognises this person from any of these numbers, but only texts the primary mobile. A number counts as verified once they text in from it, confirm it in the portal, or sign in with it.</p>
    </div>
  );
}

/* Alternate emails so a personal + board address both resolve to the same person. */
export function EmailAliasManager({ tenantId, person, readOnly, onChange }) {
  const [emails, setEmails] = useState(person?.emails || []);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { setEmails(person?.emails || []); setAdding(false); }, [person?.id]); // eslint-disable-line
  const add = async () => {
    const e = draft.trim().toLowerCase();
    if (!e.includes("@")) { toast.error("Enter a valid email"); return; }
    try {
      const r = await api.post(`/tenants/${tenantId}/people/${person.id}/emails`, { email: e });
      if (r.data.already_primary) { toast.info("That's already the primary email"); return; }
      const next = [...emails, e]; setEmails(next); onChange?.(next); setDraft(""); setAdding(false);
      toast.success("Email added");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not add"); }
  };
  const remove = async (e) => {
    try { await api.post(`/tenants/${tenantId}/people/${person.id}/emails`, { email: e, remove: true }); const next = emails.filter((x) => x !== e); setEmails(next); onChange?.(next); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not remove"); }
  };
  return (
    <div data-testid="pf-email-aliases">
      <ul className="space-y-2">
        <li className="flex items-center gap-3 rounded-xl border border-zinc-200 px-3.5 py-3">
          <Mail className="h-4 w-4 text-zinc-400 shrink-0" />
          <span className={`text-[14px] truncate ${person.email ? "" : "text-zinc-300"}`}>{person.email || "No email on file"}</span>
          <span className="text-[12px] text-zinc-500 ml-auto">Primary</span>
        </li>
        {emails.map((e) => (
          <li key={e} data-testid={`pf-alias-${e}`} className="flex items-center gap-3 rounded-xl border border-zinc-200 px-3.5 py-3">
            <Mail className="h-4 w-4 text-zinc-400 shrink-0" />
            <span className="text-[14px] truncate">{e}</span>
            {!readOnly && <button type="button" onClick={() => remove(e)} title="Remove email" className="ml-auto grid h-8 w-8 place-items-center rounded-full text-zinc-400 hover:bg-red-50 hover:text-red-700"><X className="h-3.5 w-3.5" /></button>}
          </li>
        ))}
      </ul>
      {!readOnly && !adding && <Btn size="sm" variant="ghost" className="mt-2 -ml-3" onClick={() => { setDraft(""); setAdding(true); }} data-testid="pf-add-alias"><Plus /> Add another email</Btn>}
      {adding && (
        <div data-testid="pf-alias-form" className="mt-2.5 flex items-center gap-2">
          <input data-testid="pf-alias-input" type="email" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); if (e.key === "Escape") setAdding(false); }} placeholder={`${V.grp()}.email@example.com`} className={`${inpSm} flex-1`} />
          <Btn size="sm" variant="dark" onClick={add} data-testid="pf-alias-save">Add</Btn>
          <Btn size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Btn>
        </div>
      )}
      <p className="mt-3 text-[12px] text-zinc-400">Add another email if this person uses a different address for {V.grp()}/manager work — the concierge will recognize both.</p>
    </div>
  );
}

/* Household emergency contact — one per property. */
export function EmergencyContactBlock({ tenantId, household, readOnly }) {
  const [ec, setEc] = useState(household?.emergency_contact || null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", relationship: "" });
  const [busy, setBusy] = useState(false);
  const hid = household?.id;
  useEffect(() => { setEc(household?.emergency_contact || null); setEditing(false); }, [household?.id]); // eslint-disable-line
  if (!hid) return <Empty>No property on file yet — the emergency contact lives on the property.</Empty>;
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/tenants/${tenantId}/households/${hid}/emergency-contact`, form);
      setEc(r.data.emergency_contact); setEditing(false);
      toast.success(r.data.emergency_contact ? "Emergency contact saved" : "Emergency contact cleared");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="pf-emergency-section">
      {!editing && !ec && <div className="flex items-center justify-between gap-3 flex-wrap"><Empty>No emergency contact on file for this property.</Empty>{!readOnly && <Btn size="sm" variant="dark" onClick={() => { setForm({ name: "", phone: "", relationship: "" }); setEditing(true); }} data-testid="pf-edit-emergency-btn"><Plus /> Add contact</Btn>}</div>}
      {!editing && ec && (
        <div data-testid="pf-emergency-card" className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50/50 px-4 py-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-amber-100 text-amber-800 shrink-0"><Siren className="h-4 w-4" /></span>
          <div className="min-w-0 flex-1"><div className="text-[14px] font-bold">{ec.name || "—"}{ec.relationship && <span className="font-normal text-zinc-500"> · {ec.relationship}</span>}</div>{ec.phone && <div className="font-mono text-[13px] text-zinc-700">{fmtPhone(ec.phone)}</div>}</div>
          {!readOnly && <Btn size="sm" variant="outline" onClick={() => { setForm({ name: ec?.name || "", phone: ec?.phone || "", relationship: ec?.relationship || "" }); setEditing(true); }} data-testid="pf-edit-emergency-btn"><Pencil /> Edit</Btn>}
        </div>
      )}
      {editing && (
        <div data-testid="pf-emergency-form" className="space-y-3">
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Contact name"><input data-testid="pf-emergency-name" className={inp} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Marta Aguilar" /></Field>
            <Field label="Phone"><input data-testid="pf-emergency-phone" className={`${inp} font-mono`} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(713) 555-0188" /></Field>
            <Field label="Relationship"><input data-testid="pf-emergency-rel" className={inp} value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })} placeholder="Sister" /></Field>
          </div>
          <div className="flex gap-2"><Btn size="sm" variant="dark" disabled={busy} onClick={save} data-testid="pf-emergency-save">Save</Btn><Btn size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn></div>
          <p className="text-[12px] text-zinc-400">Leave name and phone blank, then Save, to clear the contact.</p>
        </div>
      )}
      <p className="mt-4 text-[12px] text-zinc-400">Shared with the on-call manager during an emergency call. Never read to other residents.</p>
    </div>
  );
}
